import { mapObj, compose } from '@u/obj'
import {
  includes as getIncludes,
  productTable as getProductTableSchema,
} from '@r/contentful-selectors'
import { Contentful } from '@t/contentful'
import { FsCore } from '@t/fs-core'

// TODO: Type the rest of this file properly

const sys = (s: any = {}) => s.sys || {}

const getFields = ({ fields = {} }: any) => fields

export const getAuthorName = (model: any = {}) => getFields(model).name || null

export const articleSchema = (page: any = {}) => getFields(page).articleSchema || null

export const contentId = (entity: any = {}) => sys(entity).id
export const spaceId = (entity: any = {}) => sys(entity).spaceId || sys(entity).space?.sys?.id
export const contentTypeId = (entity: any = {}) =>
  sys(entity)?.contentTypeId || sys(entity)?.contentType?.sys?.id

const sysId = contentId

export const pageTitle = (page: any = {}) => getFields(page).title || ''

export const createdAt = (entity: any = {}) => sys(entity)?.createdAt
export const updatedAt = (entity: any = {}) => sys(entity)?.updatedAt

export const dataProps = (entity: any) => ({
  'data-content-type': contentTypeId(entity),
  'data-entity-id': contentId(entity),
  'data-space-id': spaceId(entity),
})

export const findInclude = <T>(includes: Contentful.Includes<T>, id: string) =>
  Object.values(includes).find((it) => contentTypeId(it) === id)

export const findIncludes = <T>(includes: Contentful.Includes<T>, id: string) =>
  Object.values(includes).filter((it) => contentTypeId(it) === id)

/* Simpify entities */
const simplifyEntitySys = (sys: any) => ({
  id: sys?.id,
  spaceId: sys?.space?.sys?.id,
  contentTypeId: sys?.contentType?.sys?.id,
  total: sys?.total,
})

const simplifyEntrySys = (entry: any) => ({ sys: { id: entry?.sys?.id } })

const simplifyEntityFields = (fields: any) =>
  Array.isArray(fields.entries)
    ? { ...fields, entries: fields.entries.map(simplifyEntrySys) }
    : fields

const simplifyEntity = ({ fields, sys }: any) => ({
  fields: simplifyEntityFields(fields),
  sys: simplifyEntitySys(sys),
})

const simplifyEntities = ({ includes, items, ...rest }: any) => ({
  ...rest,
  items: items.map(simplifyEntity),
  includes: mapObj(simplifyEntity, includes),
})

/* Simplify Product dataset */

export interface SimplifyProductDatasetsOptions {
  includes: Contentful.Includes<any>
  [key: string]: any
}
const simplifyProductDatasets = ({ includes, ...rest }: SimplifyProductDatasetsOptions) => {
  const productTables = Object.values(includes).filter((it) => contentTypeId(it) === 'productTable')

  const productDatasets = productTables
    .map(
      ({ fields }) =>
        [fields.rowCount, includes[sysId(fields.dataset)]] as [
          number,
          Contentful.Module<Contentful.ProductDatasetFields>,
        ],
    )
    .reduce((_acc, [rows, entity]) => {
      if (entity?.fields?.query) return entity
      return {
        [sysId(entity)]: {
          ...entity,
          fields: { ...entity.fields, data: entity.fields.data.slice(0, rows) },
        },
      }
    }, {})

  return {
    ...rest,
    includes: { ...includes, ...productDatasets },
  } as SimplifyProductDatasetsOptions
}

export const simplifyCtx = compose(simplifyProductDatasets, simplifyEntities)

export const getProductTableRelations = (
  state: any,
  productTable: Contentful.ProductTableComponent,
) => {
  const includes: Contentful.Includes<any> = getIncludes(state)

  return {
    productTableSchema: getProductTableSchema(
      state,
      productTable.fields.schema.sys.id,
    ) as Contentful.ProductTableSchemaComponent,
    productDataset: includes[
      productTable.fields.dataset.sys.id
    ] as Contentful.TypedModule<Contentful.ProductDatasetFields>,
  }
}

export type TableRelationsReturn = {
  productTable?: Contentful.ProductTableComponent
  productTableSchema?: Contentful.ProductTableSchemaComponent
  productDataset?: Contentful.TypedModule<Contentful.ProductDatasetFields>
}
export const getProductTableWithRelations = (state: FsCore.State, productTableId: string) => {
  const includes = getIncludes(state)
  const productTable = includes[productTableId] as Contentful.ProductTableComponent

  if (!productTable) return {}
  const { productTableSchema, productDataset } = getProductTableRelations(state, productTable)
  return {
    productTable,
    productTableSchema,
    productDataset,
  } as TableRelationsReturn
}
